import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'

import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { assets } from '@/ui/assets'
import { Address } from '@/ui/atoms/address/Address'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { AmountCell } from '@/ui/molecules/data-table/components/AmountCell'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'

interface PointsLeaderboardProps {
  position: number | null
  totalPoints: NormalizedNumber
  walletAddress: CheckedAddress
}

export function PointsLeaderboard({ position, totalPoints, walletAddress }: PointsLeaderboardProps) {
  return (
    <Panel variant="primary" className="flex flex-col gap-5">
      <Header />
      <ResponsiveDataTable
        gridTemplateColumnsClassName="grid-cols-[2fr_6fr_auto_auto] md:grid-cols-[2fr_6fr_auto_0]"
        data={[
          {
            position,
            totalPoints,
            wallet: walletAddress,
          },
        ]}
        columnDefinition={{
          position: {
            header: 'Position',
            renderCell: ({ position }) => <div className="typography-label-2">{position ? `#${position}` : '-'}</div>,
          },
          wallet: {
            header: 'Wallet',
            renderCell: ({ wallet }, mobileViewOptions) => {
              if (mobileViewOptions?.isMobileView) {
                return (
                  <div className="flex flex-row items-center justify-between">
                    <div className="typography-label-4 text-secondary">{mobileViewOptions.rowTitle}</div>
                    <div className="flex items-center gap-1">
                      <img src={assets.walletIcons.default} alt="Wallet icon" className="icon-size-xs" />
                      <Address compact address={wallet} className="typography-label-2 text-primary" />
                    </div>
                  </div>
                )
              }

              return (
                <div className="flex items-center md:gap-3">
                  <img src={assets.walletIcons.default} alt="Wallet icon" className="icon-size-md" />
                  <Address compact address={wallet} className="typography-label-2 text-primary" />
                </div>
              )
            },
          },
          totalPoints: {
            header: 'Points',
            headerAlign: 'right',
            renderCell: ({ totalPoints }, mobileViewOptions) => (
              <AmountCell
                token={USD_MOCK_TOKEN}
                amount={totalPoints}
                mobileViewOptions={mobileViewOptions}
                iconSrc={assets.points.pointsIcon}
                formattingOptions={{ showUsdValue: false, style: 'compact', zeroAmountHandling: 'show-zero' }}
              />
            ),
          },
          action: {
            header: '',
            renderCell: () => (
              <ExternalLinkButton
                to={links.sparkExternal.pointsLeaderboard}
                variant="tertiary"
                size="s"
                className="md:hidden"
              >
                View leaderboard
              </ExternalLinkButton>
            ),
          },
        }}
      />
    </Panel>
  )
}

function Header() {
  return (
    <div className="flex flex-row items-center justify-between gap-3">
      <h5 className="typography-heading-5">Leaderboard position</h5>
      <ExternalLinkButton
        to={links.sparkExternal.pointsLeaderboard}
        variant="tertiary"
        size="s"
        className="hidden md:block"
      >
        View leaderboard
      </ExternalLinkButton>
    </div>
  )
}
